/**
 * The private installer <-> EvoHome office channel for one job.
 *
 * Why this is a separate channel, not a flag on the existing one:
 * `public.job_messages` is the shared project conversation. A database trigger
 * copies the opening of every non-customer message into the homeowner's
 * notifications, so anything written there is, in effect, visible to the
 * homeowner. There is no audience column on those historical rows, and
 * guessing what each one was meant for would risk either hiding a message the
 * homeowner should see or exposing one they should not.
 *
 * So the private channel has its own table, `public.job_private_messages`,
 * with no notification, outbox or comm-log path attached to it at all.
 *
 * That table is PREPARED but NOT YET APPLIED — see
 * docs/internal/installer/backend-prepared/02_private_office_messages_up.sql.
 * Until it exists this module reports `unavailable`, and the screen says the
 * private channel is not open yet. Private text is NEVER redirected into the
 * shared conversation as a fallback.
 *
 * Identity rules:
 *   * every attempt is given a client message identifier BEFORE the write;
 *   * a stored row is accepted as this message only when the job, sender,
 *     role, text AND that identifier all match;
 *   * an answer that is lost is reconciled by looking for that identifier —
 *     never by re-sending, never with a new identifier, and never by matching
 *     on text alone;
 *   * a row returned for an identifier that carries different content is a
 *     conflict, not a success.
 */
import { supabase } from "@/integrations/supabase/client";
import {
  SAME_BROWSER_NOTE,
  digestText,
  readDurableRaw,
  removeDurable,
  writeDurable,
} from "@/lib/installerDurableStore";

export const PRIVATE_TABLE = "job_private_messages";
export const PRIVATE_READS_TABLE = "job_private_message_reads";

/** How many messages one page of history holds. */
export const PRIVATE_PAGE_SIZE = 30;

const COLUMNS =
  "id, job_id, sender_user_id, sender_role, message, client_message_id, created_at";

export interface PrivateMessage {
  id: string;
  job_id: string;
  sender_user_id: string;
  sender_role: string;
  message: string;
  client_message_id?: string | null;
  created_at: string;
}

export type PrivateSenderRole = "installer" | "admin" | "customer_service" | "consultant";

/** A stable position in history: a timestamp alone is not unique. */
export interface PrivateCursor {
  created_at: string;
  id: string;
}

export interface PrivatePage {
  /** Oldest-first, ready to render. */
  messages: PrivateMessage[];
  /** True when older messages exist beyond this page. */
  hasMore: boolean;
}

export type LoadResult =
  | { kind: "ready"; messages: PrivateMessage[]; hasMore: boolean }
  /** The channel does not exist on this system yet. */
  | { kind: "unavailable" }
  /** It exists, but this reader is not permitted. */
  | { kind: "denied" }
  /** We could not read it. Nothing is claimed about its contents. */
  | { kind: "failed"; reason: string };

export type SendResult =
  | { kind: "sent"; message: PrivateMessage }
  | { kind: "unavailable" }
  | { kind: "denied" }
  /** The office already holds this exact attempt. Nothing new was written. */
  | { kind: "already_sent"; message: PrivateMessage }
  /** The identifier names a message with different content. Do not resend. */
  | { kind: "conflict"; reason: string }
  /** We do not know whether it was stored. Reconcile, never resend blindly. */
  | { kind: "uncertain"; reason: string };

/** Reconciling one uncertain attempt, read only. Never writes. */
export type ReconcileResult =
  | { kind: "stored"; message: PrivateMessage }
  | { kind: "not_stored" }
  | { kind: "conflict"; reason: string }
  | { kind: "unknown"; reason: string };

const MISSING_MARKERS = ["42p01", "pgrst205", "does not exist", "could not find the table"];
const DENIED_MARKERS = ["42501", "pgrst301", "row-level security", "permission denied"];
const DUPLICATE_MARKERS = ["23505", "duplicate key"];

function matches(error: { code?: string | null; message?: string | null } | null, markers: string[]) {
  const text = `${error?.code ?? ""} ${error?.message ?? ""}`.toLowerCase();
  return markers.some((m) => text.includes(m));
}

export function classifyChannelError(
  error: { code?: string | null; message?: string | null } | null
): "unavailable" | "denied" | "duplicate" | "other" {
  if (!error) return "other";
  if (matches(error, MISSING_MARKERS)) return "unavailable";
  if (matches(error, DENIED_MARKERS)) return "denied";
  if (matches(error, DUPLICATE_MARKERS)) return "duplicate";
  return "other";
}

/** One identifier for one message. Frozen before the write, never reused. */
export function newClientMessageId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const hex = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += "-";
    else if (i === 14) out += "4";
    else if (i === 19) out += hex[8 + ((Math.random() * 4) | 0)];
    else out += hex[(Math.random() * 16) | 0];
  }
  return out;
}

/** Everything one attempt is: frozen together, and never edited afterwards. */
export interface PrivateAttempt {
  jobId: string;
  userId: string;
  role: PrivateSenderRole;
  text: string;
  clientMessageId: string;
}

export function freezePrivateAttempt(
  jobId: string,
  userId: string,
  role: PrivateSenderRole,
  text: string
): PrivateAttempt {
  return Object.freeze({
    jobId,
    userId,
    role,
    text: text.trim(),
    clientMessageId: newClientMessageId(),
  });
}

/** Everything the client sends is derived here; nothing else is guessed. */
export function buildPrivateInsert(attempt: PrivateAttempt) {
  return {
    job_id: attempt.jobId,
    sender_user_id: attempt.userId,
    sender_role: attempt.role,
    message: attempt.text,
    client_message_id: attempt.clientMessageId,
  };
}

/**
 * Is this stored row exactly the message this attempt sent? Every field the
 * attempt decided must be present and equal. A field the query did not return
 * is unproven, not equal.
 */
export interface PrivateProof {
  ok: boolean;
  /** Present whenever `ok` is false. */
  reason?: string;
}

export function provePrivateMessage(
  row: PrivateMessage | null | undefined,
  attempt: PrivateAttempt
): PrivateProof {
  if (!row) return { ok: false, reason: "nothing was read back" };
  if (!row.id) return { ok: false, reason: "no record identifier was returned" };
  if (row.job_id !== attempt.jobId) return { ok: false, reason: "another project" };
  if (row.sender_user_id !== attempt.userId) return { ok: false, reason: "another sender" };
  if (row.sender_role !== attempt.role) return { ok: false, reason: "a different role" };
  if (row.message !== attempt.text) return { ok: false, reason: "different words" };
  if (row.client_message_id == null) {
    return { ok: false, reason: "the attempt reference was not returned" };
  }
  if (row.client_message_id !== attempt.clientMessageId) {
    return { ok: false, reason: "a different attempt" };
  }
  return { ok: true };
}

/**
 * Newest-first from the database, returned oldest-first for reading. `before`
 * pages back through history; omit it for the most recent page.
 */
export async function loadPrivateMessages(
  jobId: string,
  options?: { before?: PrivateCursor | null; limit?: number }
): Promise<LoadResult> {
  const limit = options?.limit ?? PRIVATE_PAGE_SIZE;
  try {
    let query = (supabase.from as unknown as (t: string) => any)(PRIVATE_TABLE)
      .select(COLUMNS)
      .eq("job_id", jobId)
      // Composite ordering. Two messages written in the same instant are still
      // in a definite order, so a page boundary can never swallow one of them.
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(limit + 1);
    const before = options?.before;
    if (before) {
      query = query.or(
        `created_at.lt.${before.created_at},and(created_at.eq.${before.created_at},id.lt.${before.id})`
      );
    }
    const { data, error } = await query;
    if (error) {
      const kind = classifyChannelError(error);
      if (kind === "unavailable") return { kind: "unavailable" };
      if (kind === "denied") return { kind: "denied" };
      return { kind: "failed", reason: error.message ?? "the channel could not be read" };
    }
    const raw = (data ?? []) as unknown[];
    if (!Array.isArray(raw)) {
      return { kind: "failed", reason: "the channel returned something we could not read" };
    }
    // Something we cannot read is not an empty conversation. Saying "no
    // messages yet" when rows exist but are malformed would hide real words.
    if (!raw.every(isReadableMessage)) {
      return { kind: "failed", reason: "part of this conversation could not be read" };
    }
    const rows = raw as PrivateMessage[];
    const hasMore = rows.length > limit;
    const page = hasMore ? rows.slice(0, limit) : rows;
    return { kind: "ready", messages: [...page].reverse(), hasMore };
  } catch (thrown) {
    return {
      kind: "failed",
      reason: thrown instanceof Error ? thrown.message : "the channel could not be read",
    };
  }
}

function isReadableMessage(row: unknown): row is PrivateMessage {
  if (!row || typeof row !== "object") return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r.id === "string" &&
    r.id.length > 0 &&
    typeof r.job_id === "string" &&
    typeof r.sender_user_id === "string" &&
    typeof r.sender_role === "string" &&
    typeof r.message === "string" &&
    typeof r.created_at === "string" &&
    !Number.isNaN(new Date(r.created_at).getTime())
  );
}

/** The oldest loaded message, as a cursor for the next page back. */
export function oldestCursor(messages: PrivateMessage[]): PrivateCursor | null {
  const first = messages[0];
  return first ? { created_at: first.created_at, id: first.id } : null;
}

/** Merge an older page in front of what is on screen, without duplicates. */
export function mergeOlder(
  older: PrivateMessage[],
  current: PrivateMessage[]
): PrivateMessage[] {
  const seen = new Set(current.map((m) => m.id));
  return [...older.filter((m) => !seen.has(m.id)), ...current];
}

/** Read only. Looks for exactly this attempt; never writes anything. */
export async function reconcilePrivateAttempt(attempt: PrivateAttempt): Promise<ReconcileResult> {
  try {
    const { data, error } = await (supabase.from as unknown as (t: string) => any)(PRIVATE_TABLE)
      .select(COLUMNS)
      .eq("sender_user_id", attempt.userId)
      .eq("client_message_id", attempt.clientMessageId)
      .maybeSingle();
    if (error) {
      return { kind: "unknown", reason: error.message ?? "the channel could not be read" };
    }
    if (!data) return { kind: "not_stored" };
    const proof = provePrivateMessage(data as PrivateMessage, attempt);
    if (!proof.ok) return { kind: "conflict", reason: proof.reason ?? "it did not match" };
    return { kind: "stored", message: data as PrivateMessage };
  } catch (thrown) {
    return {
      kind: "unknown",
      reason: thrown instanceof Error ? thrown.message : "the channel could not be read",
    };
  }
}

/**
 * Send one frozen attempt. Sending the same attempt again is safe: the unique
 * client identifier means the second write is refused by the database and
 * reconciled to the message already stored.
 */
export async function sendPrivateMessage(attempt: PrivateAttempt): Promise<SendResult> {
  if (attempt.text === "") return { kind: "uncertain", reason: "there was nothing to send" };
  try {
    const { data, error } = await (supabase.from as unknown as (t: string) => any)(PRIVATE_TABLE)
      .insert(buildPrivateInsert(attempt))
      .select(COLUMNS)
      .maybeSingle();
    if (error) {
      const kind = classifyChannelError(error);
      if (kind === "unavailable") return { kind: "unavailable" };
      if (kind === "denied") return { kind: "denied" };
      if (kind === "duplicate") {
        // This exact attempt already reached the office. Read it back rather
        // than claim anything about it.
        const found = await reconcilePrivateAttempt(attempt);
        if (found.kind === "stored") return { kind: "already_sent", message: found.message };
        if (found.kind === "conflict") return { kind: "conflict", reason: found.reason };
        return { kind: "uncertain", reason: found.kind === "unknown" ? found.reason : "the office answer did not match" };
      }
      return { kind: "uncertain", reason: error.message ?? "the answer was lost" };
    }
    const proof = provePrivateMessage(data as PrivateMessage, attempt);
    if (!proof.ok) return { kind: "uncertain", reason: proof.reason ?? "it did not match" };
    return { kind: "sent", message: data as PrivateMessage };
  } catch (thrown) {
    return {
      kind: "uncertain",
      reason: thrown instanceof Error ? thrown.message : "the answer was lost",
    };
  }
}

/** When this reader last opened the channel. Absent marker means "never". */
export async function readLastReadAt(
  jobId: string,
  userId: string
): Promise<{ kind: "ok"; at: string | null } | { kind: "unknown" }> {
  try {
    const { data, error } = await (supabase.from as unknown as (t: string) => any)(
      PRIVATE_READS_TABLE
    )
      .select("last_read_at")
      .eq("job_id", jobId)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) return { kind: "unknown" };
    return { kind: "ok", at: (data?.last_read_at as string | undefined) ?? null };
  } catch {
    return { kind: "unknown" };
  }
}

/**
 * Move the read marker to the newest message this reader has actually been
 * shown — never to "now". Using the clock would silently mark a message read
 * that arrived while the page was loading and was never on screen.
 *
 * The marker only ever moves forwards, so an older page being opened cannot
 * push it backwards and resurrect messages as unread.
 */
export function readBoundary(
  messages: PrivateMessage[],
  previousMarker: string | null
): string | null {
  let newest: string | null = null;
  for (const m of messages) {
    if (!newest || new Date(m.created_at).getTime() > new Date(newest).getTime()) {
      newest = m.created_at;
    }
  }
  if (!newest) return previousMarker;
  if (previousMarker && new Date(previousMarker).getTime() >= new Date(newest).getTime()) {
    return previousMarker;
  }
  return newest;
}

/** Best effort. A failed marker never hides a message and is never reported as an error to the user. */
export async function markPrivateChannelRead(
  jobId: string,
  userId: string,
  seenUpTo: string
): Promise<void> {
  if (!seenUpTo) return;
  try {
    await (supabase.from as unknown as (t: string) => any)(PRIVATE_READS_TABLE).upsert(
      { job_id: jobId, user_id: userId, last_read_at: seenUpTo },
      { onConflict: "job_id,user_id" }
    );
  } catch {
    /* the marker is a convenience, never a gate */
  }
}

/** How many messages from somebody else arrived after this reader last looked. */
export function countUnread(
  messages: PrivateMessage[],
  userId: string | null | undefined,
  lastReadAt: string | null
): number {
  return messages.filter((m) => {
    if (m.sender_user_id === userId) return false;
    if (!lastReadAt) return true;
    return new Date(m.created_at).getTime() > new Date(lastReadAt).getTime();
  }).length;
}

export interface UnreadSummary {
  count: number;
  /**
   * False when older messages were never loaded, so the count is "at least
   * this many" rather than a count of the whole conversation.
   */
  exact: boolean;
}

/**
 * Unread from what has actually been read back. When history has not been
 * fully loaded the count is explicitly a minimum, never presented as the total
 * for the thread.
 */
export function summariseUnread(
  messages: PrivateMessage[],
  userId: string | null | undefined,
  lastReadAt: string | null,
  hasMore: boolean
): UnreadSummary {
  const count = countUnread(messages, userId, lastReadAt);
  return { count, exact: !hasMore };
}

/* ------------------------------------------------------------------ */
/* Attempts whose answer was lost                                       */
/* ------------------------------------------------------------------ */

/**
 * Attempts are held per account+job for as long as this tab lives, so moving
 * to another job and back does not forget that something may already have
 * reached the office. Nothing here is written to disk: the words are private
 * and a stored copy could outlive the person signing out.
 */
const unresolvedAttempts = new Map<string, PrivateAttempt>();

export const PRIVATE_ATTEMPT_IDENTITY_VERSION = 1;

/**
 * The durable trace of an attempt whose answer was lost.
 *
 * It holds NO words. Only the reference the office would have stored, and a
 * one-way fingerprint of the text, so that after the tab is closed the same
 * message can be recognised when it is typed again — and so that nobody else
 * at this device can replay it, because they would have to type the identical
 * words to produce the same fingerprint.
 */
export interface PrivateAttemptIdentity {
  version: number;
  userId: string;
  jobId: string;
  clientMessageId: string;
  textDigest: string;
  startedAt: number;
}

export const PRIVATE_RECOVERY_NOTE = `A message here was sent but never confirmed. Its reference is remembered so the office cannot receive it twice. The words themselves are never kept on this device, so type them again exactly to check what happened. ${SAME_BROWSER_NOTE}`;

export const PRIVATE_RECOVERY_MISMATCH =
  "Those are not the same words as the message waiting to be checked, so nothing has been checked. Type the original message exactly, or ask the EvoHome Improvements office what they hold for this project.";

export function privateAttemptKey(userId: string | null | undefined, jobId: string): string {
  return `evohome.installer.private.v${PRIVATE_ATTEMPT_IDENTITY_VERSION}:${userId ?? ""}:${jobId}`;
}

/** Called before the write, so a lost answer always leaves a trace. */
export async function persistAttemptIdentity(
  attempt: PrivateAttempt
): Promise<{ ok: boolean; reason?: string }> {
  const digest = await digestText(attempt.text);
  if (!digest) {
    return {
      ok: false,
      reason:
        "This device cannot fingerprint your message, so it cannot be recognised again after the tab is closed. Stay on this page until it is confirmed sent.",
    };
  }
  const written = writeDurable(privateAttemptKey(attempt.userId, attempt.jobId), {
    version: PRIVATE_ATTEMPT_IDENTITY_VERSION,
    userId: attempt.userId,
    jobId: attempt.jobId,
    clientMessageId: attempt.clientMessageId,
    textDigest: digest,
    startedAt: Date.now(),
  } satisfies PrivateAttemptIdentity);
  return written.ok ? { ok: true } : { ok: false, reason: written.reason };
}

export function loadAttemptIdentity(
  userId: string | null | undefined,
  jobId: string
): PrivateAttemptIdentity | null {
  const read = readDurableRaw(privateAttemptKey(userId, jobId));
  if (!read.ok || !read.value) return null;
  try {
    const parsed = JSON.parse(read.value) as Partial<PrivateAttemptIdentity>;
    if (
      !parsed ||
      parsed.version !== PRIVATE_ATTEMPT_IDENTITY_VERSION ||
      parsed.userId !== userId ||
      parsed.jobId !== jobId ||
      typeof parsed.clientMessageId !== "string" ||
      parsed.clientMessageId === "" ||
      typeof parsed.textDigest !== "string" ||
      parsed.textDigest === ""
    ) {
      return null;
    }
    return {
      version: PRIVATE_ATTEMPT_IDENTITY_VERSION,
      userId,
      jobId,
      clientMessageId: parsed.clientMessageId,
      textDigest: parsed.textDigest,
      startedAt: typeof parsed.startedAt === "number" ? parsed.startedAt : 0,
    };
  } catch {
    return null;
  }
}

export function clearAttemptIdentity(userId: string | null | undefined, jobId: string): void {
  removeDurable(privateAttemptKey(userId, jobId));
}

/**
 * Rebuild the original attempt from re-typed words. The reference is reused
 * exactly, so checking or sending again cannot produce a second message — and
 * different words are refused rather than sent under the old reference.
 */
export async function restoreAttemptFromIdentity(
  identity: PrivateAttemptIdentity,
  role: PrivateSenderRole,
  typedText: string
): Promise<{ ok: true; attempt: PrivateAttempt } | { ok: false; reason: string }> {
  const text = typedText.trim();
  if (text === "") return { ok: false, reason: PRIVATE_RECOVERY_MISMATCH };
  const digest = await digestText(text);
  if (!digest || digest !== identity.textDigest) {
    return { ok: false, reason: PRIVATE_RECOVERY_MISMATCH };
  }
  return {
    ok: true,
    attempt: Object.freeze({
      jobId: identity.jobId,
      userId: identity.userId,
      role,
      text,
      clientMessageId: identity.clientMessageId,
    }),
  };
}

export function attemptScope(userId: string | null | undefined, jobId: string): string {
  return `${userId ?? ""}:${jobId}`;
}

export function rememberAttempt(attempt: PrivateAttempt): void {
  unresolvedAttempts.set(attemptScope(attempt.userId, attempt.jobId), attempt);
}

export function recallAttempt(userId: string | null | undefined, jobId: string): PrivateAttempt | null {
  return unresolvedAttempts.get(attemptScope(userId, jobId)) ?? null;
}

export function forgetAttempt(userId: string | null | undefined, jobId: string): void {
  unresolvedAttempts.delete(attemptScope(userId, jobId));
  clearAttemptIdentity(userId, jobId);
}

/**
 * Signing out or handing the device over clears every held attempt, including
 * the words themselves, which only ever lived in memory.
 *
 * The durable reference is deliberately NOT deleted here: it is what stops the
 * same person, signing back in, sending a second copy of a message the office
 * may already hold. It carries no words, and the next person cannot use it —
 * checking or resending requires typing the identical message, which they do
 * not have.
 */
export function forgetAllAttempts(): void {
  unresolvedAttempts.clear();
}

export const PRIVATE_UNAVAILABLE_MESSAGE =
  "The private office channel is not open on this system yet. Nothing you type here has been sent, and it has not been added to the shared project conversation. Please telephone or email the EvoHome Improvements office for anything private.";

export const PRIVATE_DENIED_MESSAGE =
  "You do not have access to the private office channel for this project.";

export const PRIVATE_UNCERTAIN_MESSAGE =
  "We could not confirm whether your message reached the office. It has not been sent again. Use “Check whether it arrived” — this uses the same reference, so the office cannot receive it twice.";

export const PRIVATE_CONFLICT_MESSAGE =
  "The office holds something different against this message. Nothing further has been sent, and nothing more can be sent on this project until it has been looked at. Please reload the channel and check what the office holds.";

/**
 * Absence on a read while a write may still be in progress does not prove the
 * message never arrived, so the wording never says it did not.
 */
export const PRIVATE_NOT_FOUND_MESSAGE =
  "We have not found it yet. That does not prove it failed to reach the office. Your words are still in the box below, and sending again uses the same reference so the office cannot receive it twice.";

export const PRIVATE_ALREADY_SENT_MESSAGE =
  "The office already had this message. It has not been recorded twice.";

export const PRIVATE_AUDIENCE_NOTE =
  "Private to you and the EvoHome Improvements office. The homeowner never sees this channel and is never notified about it.";

export const SHARED_AUDIENCE_NOTE =
  "Shared with the homeowner: they are alerted whenever you post here, and the alert shows the beginning of your message.";

/* ------------------------------------------------------------------ */
/* Office-wide view: which projects have private messages waiting      */
/* ------------------------------------------------------------------ */

export interface OfficeUnreadJob {
  jobId: string;
  count: number;
  latestAt: string;
}

export type OfficeUnreadState =
  | { kind: "unavailable"; message: string }
  | { kind: "error"; message: string }
  | { kind: "ready"; jobs: OfficeUnreadJob[]; complete: boolean };

/** How many recent messages are examined. Beyond this the count is partial. */
export const OFFICE_UNREAD_SCAN_LIMIT = 200;

export const OFFICE_UNREAD_UNAVAILABLE =
  "The private installer channel is not switched on yet, so there is nothing to show here. This is not the same as having no messages waiting.";

/**
 * A truthful, text-free summary of private conversations needing an office
 * reply. It never returns message wording, and it never guesses: if the
 * channel cannot be read, that is said plainly rather than shown as zero.
 */
export async function loadOfficePrivateUnread(userId: string | null | undefined): Promise<OfficeUnreadState> {
  if (!userId) return { kind: "error", message: "Sign in again to see waiting private messages." };
  try {
    const { data, error } = await (supabase.from as unknown as (t: string) => any)(PRIVATE_TABLE)
      .select("job_id, created_at, sender_user_id, sender_role")
      .order("created_at", { ascending: false })
      .limit(OFFICE_UNREAD_SCAN_LIMIT + 1);
    if (error) {
      const code = (error as { code?: string }).code ?? "";
      const missing = code === "42P01" || /does not exist|schema cache/i.test(error.message ?? "");
      return missing
        ? { kind: "unavailable", message: OFFICE_UNREAD_UNAVAILABLE }
        : { kind: "error", message: "The private channel could not be read just now, so this list is not being shown rather than shown wrongly." };
    }
    const rows = Array.isArray(data) ? data : [];
    const complete = rows.length <= OFFICE_UNREAD_SCAN_LIMIT;
    const scanned = rows.slice(0, OFFICE_UNREAD_SCAN_LIMIT);

    const { data: marks } = await (supabase.from as unknown as (t: string) => any)(PRIVATE_READS_TABLE)
      .select("job_id, last_read_at")
      .eq("user_id", userId);
    const markers = new Map<string, number>();
    for (const m of Array.isArray(marks) ? marks : []) {
      const at = new Date(m?.last_read_at ?? 0).getTime();
      if (m?.job_id && Number.isFinite(at)) markers.set(m.job_id, at);
    }

    const byJob = new Map<string, OfficeUnreadJob>();
    for (const row of scanned) {
      const jobId = row?.job_id;
      const at = row?.created_at;
      if (typeof jobId !== "string" || typeof at !== "string") continue;
      if (row.sender_user_id === userId) continue;
      if (row.sender_role !== "installer") continue;
      const seen = markers.get(jobId);
      if (seen !== undefined && new Date(at).getTime() <= seen) continue;
      const existing = byJob.get(jobId);
      if (existing) existing.count += 1;
      else byJob.set(jobId, { jobId, count: 1, latestAt: at });
    }
    return {
      kind: "ready",
      jobs: [...byJob.values()].sort(
        (a, b) => new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime()
      ),
      complete,
    };
  } catch {
    return { kind: "error", message: "The private channel could not be read just now, so this list is not being shown rather than shown wrongly." };
  }
}
